import { Button, Card, message, Modal, Input, Tag, List, Empty, Descriptions, Tabs } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { $ } from '../lib/api';

const IMPORT_TYPE_LABEL: Record<string, string> = {
  customers: '客户主表', follow_ups: '跟进记录', contracts: '合同', invoices: '发票',
  payments: '收款', shipments: '邮寄', materials: '学员资料', orders: '订单',
};

// entityType → 中文标签（key 用单数对齐后端 entity_type）。
const ENTITY_LABEL: Record<string, string> = {
  order: '订单', data_import: '数据导入', contract: '合同', invoice: '发票',
  payment: '定金/收款', payment_record: '定金/收款', material: '学员资料',
  follow_up: '跟进记录', follow_up_next_contact: '下次沟通时间', stage_advance: '阶段推进',
  commission: '提成', customer: '客户', shipment_waive: '无需教材',
};
function entityTag(t: string): string { return ENTITY_LABEL[t] ?? t; }

export function ApprovalsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState('all');
  const { data } = useQuery({
    queryKey: ['approvals'],
    queryFn: () => $.get('/approvals/pending'),
    refetchInterval: 15_000, // 15s 自动刷新（主管可能在这页守着）
  });

  // 通过：根据 entityType 走不同路由（data_import 暂保留 /imports/:jobId/approve 兼容，
  // 其他实体统一走 /approvals/:approvalId/approve，后端按 entityType 触发对应回调）
  const approve = useMutation({
    mutationFn: ({ a, comments }: { a: any; comments?: string }) =>
      a.entityType === 'data_import'
        ? $.post(`/imports/${a.entityId}/approve`, { comments })
        : $.post(`/approvals/${a.id}/approve`, { comments }),
    onSuccess: () => { message.success('已通过'); qc.invalidateQueries({ queryKey: ['approvals'] }); },
  });

  const [rejectTarget, setRejectTarget] = useState<any | null>(null);
  const [reason, setReason] = useState('');
  const reject = useMutation({
    mutationFn: ({ a, reason }: { a: any; reason: string }) =>
      a.entityType === 'data_import'
        ? $.post(`/imports/${a.entityId}/reject`, { reason })
        : $.post(`/approvals/${a.id}/reject`, { reason }),
    onSuccess: () => {
      message.success('已驳回');
      setRejectTarget(null); setReason('');
      qc.invalidateQueries({ queryKey: ['approvals'] });
    },
  });

  const items: any[] = data?.items ?? [];
  // B0：按 entityType 客户端分组成多标签页（纯前端过滤，复用现有 /approvals/pending，不需后端新接口）
  const groups = items.reduce((m: Record<string, number>, a: any) => {
    m[a.entityType] = (m[a.entityType] ?? 0) + 1; return m;
  }, {} as Record<string, number>);
  // 固定常驻分类标签（已启用的全部 6 类审批）：即使该类型 0 条也显示标签+数字，保证审批人始终看得到分类、知道去哪审；
  // 再并入当前 pending 数据里出现的其它类型（前瞻：后续批接入新类型时自动多出标签）。
  const FIXED_TAB_TYPES = ['order', 'contract', 'invoice', 'payment_record', 'stage_advance', 'follow_up_next_contact', 'shipment_waive', 'data_import'];
  const tabTypes = Array.from(new Set([...FIXED_TAB_TYPES, ...Object.keys(groups)]));
  const tabItems = [
    { key: 'all', label: `全部（${items.length}）` },
    ...tabTypes.map((k) => ({ key: k, label: `${entityTag(k)}（${groups[k] ?? 0}）` })),
  ];
  const activeKey = tabItems.some((t) => t.key === tab) ? tab : 'all';
  const shown = activeKey === 'all' ? items : items.filter((a) => a.entityType === activeKey);

  return (
    <Card title="待我审批">
      <Tabs activeKey={activeKey} onChange={setTab} items={tabItems} />
      {!shown.length ? <Empty description="无待审批" /> : (
        <List
          dataSource={shown}
          renderItem={(a: any) => (
            <List.Item
              actions={[
                <Button type="primary" key="ok" loading={approve.isPending} onClick={() => approve.mutate({ a })}>
                  通过
                </Button>,
                <Button danger key="no" onClick={() => { setRejectTarget(a); setReason(''); }}>驳回</Button>,
              ]}
            >
              <List.Item.Meta
                title={
                  <>
                    {a.title}
                    <Tag style={{ marginLeft: 8 }}>{entityTag(a.entityType)}</Tag>
                    {a.urgency ? <Tag color="red">加急</Tag> : null}
                    {a.autoApproveAt ? <Tag color="orange">{autoLeft(a.autoApproveAt)}</Tag> : null}
                  </>
                }
                description={
                  <div>
                    <div style={{ color: '#888', fontSize: 12 }}>
                      审批单 {a.approvalCode} · 步骤 {a.currentStep}/{a.totalSteps} · 提交 {fmtDate(a.submittedAt)}
                    </div>
                    {a.entityType === 'data_import' && a.metadataJson && (
                      <ImportApprovalDetails meta={a.metadataJson} />
                    )}
                    {a.entityType === 'order' && a.metadataJson && (
                      <OrderApprovalDetails meta={a.metadataJson} />
                    )}
                    {a.entityType === 'contract' && a.metadataJson && (
                      <ContractApprovalDetails meta={a.metadataJson} />
                    )}
                    {a.entityType === 'invoice' && a.metadataJson && (
                      <InvoiceApprovalDetails meta={a.metadataJson} />
                    )}
                    {a.entityType === 'payment_record' && a.metadataJson && (
                      <PaymentRecordApprovalDetails meta={a.metadataJson} />
                    )}
                    {a.entityType === 'follow_up_next_contact' && a.metadataJson && (
                      <NextContactApprovalDetails meta={a.metadataJson} />
                    )}
                    {a.entityType === 'stage_advance' && a.metadataJson && (
                      <StageAdvanceApprovalDetails meta={a.metadataJson} />
                    )}
                    {a.entityType === 'shipment_waive' && a.metadataJson && (
                      <ShipmentWaiveApprovalDetails meta={a.metadataJson} />
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
      <Modal
        open={rejectTarget !== null}
        title="驳回理由"
        onCancel={() => setRejectTarget(null)}
        onOk={() => {
          if (!reason.trim()) { message.warning('驳回理由必填'); return; }
          rejectTarget && reject.mutate({ a: rejectTarget, reason: reason.trim() });
        }}
        okButtonProps={{ loading: reject.isPending }}
      >
        <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="必填 — 销售员看得到，请写清楚原因" />
      </Modal>
    </Card>
  );
}

function ImportApprovalDetails({ meta }: { meta: any }) {
  const importLabel = IMPORT_TYPE_LABEL[meta.importType] ?? meta.importType;
  return (
    <div style={{ marginTop: 8, padding: 10, background: '#fafafa', borderRadius: 4 }}>
      <Descriptions size="small" column={2} colon={false}
        labelStyle={{ color: '#888', fontSize: 12, width: 80 }}
        contentStyle={{ fontSize: 12 }}>
        <Descriptions.Item label="提交人">{meta.submitterName ?? `#${meta.submitterId}`}</Descriptions.Item>
        <Descriptions.Item label="导入类型">{importLabel}</Descriptions.Item>
        <Descriptions.Item label="文件名" span={2}>
          <span style={{ fontFamily: 'monospace' }}>{meta.fileName ?? '-'}</span>
        </Descriptions.Item>
        <Descriptions.Item label="总行数">{meta.totalRows} 行</Descriptions.Item>
        <Descriptions.Item label="冲突策略">
          <Tag>{({ skip: '跳过', overwrite: '覆盖', transfer: '转让' } as any)[meta.conflictStrategy] ?? meta.conflictStrategy}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="dry-run">
          <Tag color="green">将导入 {meta.dryRunSuccess}</Tag>
          {meta.dryRunSkip > 0 ? <Tag>跳过 {meta.dryRunSkip}</Tag> : null}
          {meta.dryRunFail > 0 ? <Tag color="red">失败 {meta.dryRunFail}</Tag> : null}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
}

// B3 件1：订单审批详情 + AI 预审展示（AI 挂了 aiPreReview 为占位/缺失，可选链兜底不渲染分析块）
function OrderApprovalDetails({ meta }: { meta: any }) {
  const ai = meta.aiPreReview;
  const aiOk = ai && typeof ai === 'object' && ai.status !== 'ai_unavailable' && ai.recommendation;
  const RISK_COLOR: Record<string, string> = { low: 'green', medium: 'orange', high: 'red' };
  return (
    <div style={{ marginTop: 8, padding: 10, background: '#fafafa', borderRadius: 4 }}>
      <Descriptions size="small" column={2} colon={false}
        labelStyle={{ color: '#888', fontSize: 12, width: 80 }}
        contentStyle={{ fontSize: 12 }}>
        <Descriptions.Item label="提交人">{meta.submitterName ?? `#${meta.submitterId ?? '-'}`}</Descriptions.Item>
        <Descriptions.Item label="客户">{meta.customerName ?? (meta.customerId ? `#${meta.customerId}` : '-')}</Descriptions.Item>
        <Descriptions.Item label="班型">{meta.classType ?? '-'}</Descriptions.Item>
        <Descriptions.Item label="金额">¥{meta.amount != null ? Number(meta.amount).toLocaleString() : '-'}</Descriptions.Item>
      </Descriptions>
      <div style={{ marginTop: 8, fontSize: 12 }}>
        <span style={{ color: '#888' }}>🤖 AI 预审：</span>
        {aiOk ? (
          <>
            <Tag color={RISK_COLOR[ai.risk_level] ?? 'default'}>
              {({ low: '低风险', medium: '中风险', high: '高风险' } as any)[ai.risk_level] ?? ai.risk_level}
              {ai.risk_score != null ? ` · ${ai.risk_score}` : ''}
            </Tag>
            <Tag>{ai.recommendation}</Tag>
            {ai.reason ? <span style={{ color: '#555' }}>{ai.reason}</span> : null}
            {ai.price_check ? <div style={{ color: '#888', marginTop: 4 }}>报价核对：{ai.price_check}</div> : null}
            {Array.isArray(ai.risk_points) && ai.risk_points.length ? (
              <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: '#555' }}>
                {ai.risk_points.map((p: string, i: number) => <li key={i}>{p}</li>)}
              </ul>
            ) : null}
          </>
        ) : (
          <Tag color="default">AI 暂不可用（不影响审批）</Tag>
        )}
      </div>
    </div>
  );
}

// 审批A：通用小详情框（合同/发票/收款/下次沟通）
function DescBox({ children }: { children: ReactNode }) {
  return (
    <div style={{ marginTop: 8, padding: 10, background: '#fafafa', borderRadius: 4 }}>
      <Descriptions size="small" column={2} colon={false}
        labelStyle={{ color: '#888', fontSize: 12, width: 80 }} contentStyle={{ fontSize: 12 }}>
        {children}
      </Descriptions>
    </div>
  );
}

// 审批A：合同审批详情（项3 metadata：submitterName/amount/serviceType/customerId）
function ContractApprovalDetails({ meta }: { meta: any }) {
  return (
    <DescBox>
      <Descriptions.Item label="提交人">{meta.submitterName ?? `#${meta.submitterId ?? '-'}`}</Descriptions.Item>
      <Descriptions.Item label="客户">{meta.customerName ?? (meta.customerId ? `#${meta.customerId}` : '-')}</Descriptions.Item>
      <Descriptions.Item label="班型">{meta.serviceType ?? '-'}</Descriptions.Item>
      <Descriptions.Item label="金额">¥{meta.amount != null ? Number(meta.amount).toLocaleString() : '-'}</Descriptions.Item>
    </DescBox>
  );
}

// 审批A：发票开票审批详情（metadata：submitterName/amount/headerName）
function InvoiceApprovalDetails({ meta }: { meta: any }) {
  return (
    <DescBox>
      <Descriptions.Item label="提交人">{meta.submitterName ?? `#${meta.submitterId ?? '-'}`}</Descriptions.Item>
      <Descriptions.Item label="客户">{meta.customerName ?? (meta.customerId ? `#${meta.customerId}` : '-')}</Descriptions.Item>
      <Descriptions.Item label="金额">¥{meta.amount != null ? Number(meta.amount).toLocaleString() : '-'}</Descriptions.Item>
      <Descriptions.Item label="抬头" span={2}>{meta.headerName ?? '-'}</Descriptions.Item>
    </DescBox>
  );
}

// 审批A：收款流水审批详情（metadata：submitterName/amount/type/ocrStatus/paymentId）
function PaymentRecordApprovalDetails({ meta }: { meta: any }) {
  const ocr: Record<string, string> = { pass: 'OCR 一致', mismatch: 'OCR 金额不符', failed: 'OCR 未识别' };
  const ocrColor: Record<string, string> = { pass: 'green', mismatch: 'red', failed: 'orange' };
  return (
    <DescBox>
      <Descriptions.Item label="提交人">{meta.submitterName ?? `#${meta.submitterId ?? '-'}`}</Descriptions.Item>
      <Descriptions.Item label="客户">{meta.customerName ?? (meta.customerId ? `#${meta.customerId}` : '-')}</Descriptions.Item>
      <Descriptions.Item label="类型">{meta.type === 'deposit' ? '定金' : meta.type === 'refund' ? '退款' : '收款'}</Descriptions.Item>
      <Descriptions.Item label="金额">¥{meta.amount != null ? Number(meta.amount).toLocaleString() : '-'}</Descriptions.Item>
      <Descriptions.Item label="OCR 对账">
        {meta.ocrStatus ? <Tag color={ocrColor[meta.ocrStatus] ?? 'default'}>{ocr[meta.ocrStatus] ?? meta.ocrStatus}</Tag> : '-'}
      </Descriptions.Item>
    </DescBox>
  );
}

// 审批A：下次沟通时间审批详情（项2 metadata：submitterName/customerId/stage/pendingAt）
function NextContactApprovalDetails({ meta }: { meta: any }) {
  return (
    <DescBox>
      <Descriptions.Item label="提交人">{meta.submitterName ?? `#${meta.submitterId ?? '-'}`}</Descriptions.Item>
      <Descriptions.Item label="阶段">{meta.stage ?? '-'}</Descriptions.Item>
      <Descriptions.Item label="客户">{meta.customerName ?? (meta.customerId ? `#${meta.customerId}` : '-')}</Descriptions.Item>
      <Descriptions.Item label="拟定下次时间">{meta.pendingAt ? fmtDate(meta.pendingAt) : '-'}</Descriptions.Item>
    </DescBox>
  );
}

// 软改硬(inc4)：阶段推进审批详情（metadata：fromStage/targetStage/customerName/hasPendingTime/pendingAt）
function StageAdvanceApprovalDetails({ meta }: { meta: any }) {
  return (
    <DescBox>
      <Descriptions.Item label="提交人">{meta.submitterName ?? `#${meta.submitterId ?? '-'}`}</Descriptions.Item>
      <Descriptions.Item label="客户">{meta.customerName ?? (meta.customerId ? `#${meta.customerId}` : '-')}</Descriptions.Item>
      <Descriptions.Item label="阶段推进" span={2}>
        <Tag color="blue">{meta.fromStage ?? '-'}</Tag> → <Tag color="green">{meta.targetStage ?? '-'}</Tag>
      </Descriptions.Item>
      {meta.hasPendingTime && meta.pendingAt && (
        <Descriptions.Item label="下次沟通" span={2}>{fmtDate(meta.pendingAt)}（随阶段一起生效）</Descriptions.Item>
      )}
    </DescBox>
  );
}

// 无需教材审批详情（metadata：submitterName/customerName）——客户不需要教材时 sales 发起，主管单步审，通过推进 6_shipment→7_materials
function ShipmentWaiveApprovalDetails({ meta }: { meta: any }) {
  return (
    <DescBox>
      <Descriptions.Item label="提交人">{meta.submitterName ?? `#${meta.submitterId ?? '-'}`}</Descriptions.Item>
      <Descriptions.Item label="客户">{meta.customerName ?? (meta.customerId ? `#${meta.customerId}` : '-')}</Descriptions.Item>
    </DescBox>
  );
}

// 软提示审批：距离自动通过还剩多少小时（B0 阶段无软提示单，autoApproveAt 恒空、不显示）
function autoLeft(iso: string): string {
  const remaining = Math.max(0, (new Date(iso).getTime() - Date.now()) / 3_600_000);
  return `${remaining.toFixed(1)}h 后自动通过`;
}

function fmtDate(iso?: string): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
